import type { RouteObject } from "react-router";

import { Outlet } from "react-router";
import { lazy, Suspense } from "react";

import { CONFIG } from "src/global-config";
import { DashboardLayout } from "src/layouts/dashboard";

import { LoadingScreen } from "src/components/loading-screen";

import { AuthGuard } from "src/auth/guard";

import { usePathname } from "../hooks";

// ----------------------------------------------------------------------

const DashboardPage = lazy(() => import("src/pages/dashboard/dashboard"));
const UserPage = lazy(() => import("src/pages/dashboard/user"));
const ReportedUserPage = lazy(
  () => import("src/pages/dashboard/reported-user")
);
const PostPage = lazy(() => import("src/pages/dashboard/post"));
const ReelPage = lazy(() => import("src/pages/dashboard/reel"));
const ReportedPostPage = lazy(
  () => import("src/pages/dashboard/reported-post")
);
const ReportedReelPage = lazy(
  () => import("src/pages/dashboard/reported-reel")
); // ✅ added
const OpenCallPage = lazy(() => import("src/pages/dashboard/opencall"));
const OpenCallDetailsPage = lazy(
  () => import("src/pages/dashboard/opencall/opencall-details")
);
const OpenCallAuditionPage = lazy(
  () => import("src/pages/dashboard/opencall/opencall-auditions")
);
const ReportedAuditionPage = lazy(
  () => import("src/pages/dashboard/reported-audition")
);
const LivePage = lazy(() => import("src/pages/dashboard/live"));
const StarFeedPage = lazy(() => import("src/pages/dashboard/star-feed"));
const FeaturePage = lazy(
  () => import("src/pages/dashboard/feature-permission")
);
const ChangePasswordPage = lazy(
  () => import("src/pages/dashboard/change-password")
);

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: "/",
    element: CONFIG.auth.skip ? (
      dashboardLayout()
    ) : (
      <AuthGuard>{dashboardLayout()}</AuthGuard>
    ),
    children: [
      { path: "dashboard", element: <DashboardPage />, index: true },
      { path: "users", element: <UserPage /> },
      { path: "reported-users", element: <ReportedUserPage /> },
      { path: "posts", element: <PostPage /> },
      { path: "reported-posts", element: <ReportedPostPage /> },
      { path: "reels", element: <ReelPage /> },
      { path: "reported-reels", element: <ReportedReelPage /> },
      {
        path: "opencall",
        children: [
          { element: <OpenCallPage />, index: true },
          { path: ":ocid", element: <OpenCallDetailsPage /> },
          { path: "auditions/:ocid", element: <OpenCallAuditionPage /> },
        ],
      },
      { path: "live", element: <LivePage /> },
      { path: "star-feed", element: <StarFeedPage /> },
      { path: "feature-permissions", element: <FeaturePage /> },
      { path: "reported-auditions", element: <ReportedAuditionPage /> },
      { path: "change-password", element: <ChangePasswordPage /> },
    ],
  },
];
